import React, { useState, useEffect } from 'react';

const USER_BACKEND_URL = 'https://app.loopai.in';

const ASSET_CATEGORIES = {
  characters: [
    { key: 'actors', label: 'Actors' },
    { key: 'costumes', label: 'Costumes' },
  ],
  elements: [
    { key: 'products', label: 'Products' },
    { key: 'logos', label: 'Logos' },
    { key: 'costumes', label: 'Costumes' },
  ],
  'logos-only': [
    { key: 'logos', label: 'Logos' },
  ],
  narrator: [
    { key: 'actors', label: 'Actors' },
  ],
  all: [
    { key: 'actors', label: 'Actors' },
    { key: 'costumes', label: 'Costumes' },
    { key: 'products', label: 'Products' },
    { key: 'logos', label: 'Logos' },
  ],
  media: [
    { key: 'audio', label: 'Audio' },
    { key: 'videos', label: 'Videos' },
  ],
};

const isMediaTab = (tab) => tab === 'media';

async function fetchUserAssets(assetType) {
  const apiKey = sessionStorage.getItem('apiKey');
  const userEmail = sessionStorage.getItem('userEmail');

  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['X-API-Key'] = apiKey;
  if (userEmail) headers['X-User-Email'] = userEmail;

  const response = await fetch(`${USER_BACKEND_URL}/api/user-assets/${assetType}`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`Failed to fetch assets: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  return data.assets || [];
}

export const UserAssetsModal = ({ onSelect, onClose, activeTab }) => {
  const categories = ASSET_CATEGORIES[activeTab] || ASSET_CATEGORIES.characters;
  const [selectedCategory, setSelectedCategory] = useState(categories[0].key);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAssets([]);

    fetchUserAssets(selectedCategory)
      .then((data) => {
        if (!cancelled) setAssets(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCategory]);

  const getImageUrl = (asset) => asset.image_url || asset.imageUrl || asset.url
    || asset.audio_url || asset.audioUrl || asset.video_url || asset.videoUrl || asset.file_url || '';

  const handleSelect = (asset) => {
    const url = getImageUrl(asset);
    if (!url) return;
    // Feb 2026 — pass the FULL asset object so callers can pull metadata
    // (voice_id, name, description, actor_id, image_gallery, category, …)
    // in addition to the image URL. Backwards-compatible: legacy callers
    // that only read `imageUrl` ignore the second arg.
    onSelect(url, asset);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="user-assets-modal-overlay"
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="user-assets-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">User Assets</h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-700 rounded-full transition-colors"
            data-testid="user-assets-close-btn"
          >
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Category Tabs */}
        {categories.length > 1 && (
          <div className="flex gap-1 px-5 pt-3">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setSelectedCategory(cat.key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedCategory === cat.key
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                }`}
                data-testid={`asset-tab-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 min-h-[300px]">
          {loading && (
            <div className="flex items-center justify-center h-48" data-testid="assets-loading">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-gray-400 text-sm">Loading assets...</span>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-center justify-center h-48" data-testid="assets-error">
              <div className="text-center">
                <p className="text-red-400 text-sm mb-3">{error}</p>
                <button
                  onClick={() => {
                    setLoading(true);
                    setError(null);
                    fetchUserAssets(selectedCategory)
                      .then(setAssets)
                      .catch((err) => setError(err.message))
                      .finally(() => setLoading(false));
                  }}
                  className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-md transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && assets.length === 0 && (
            <div className="flex items-center justify-center h-48" data-testid="assets-empty">
              <div className="text-center">
                <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 text-sm">No assets found in this category.</p>
                <p className="text-gray-600 text-xs mt-1">Upload assets from your dashboard at app.loopai.in</p>
              </div>
            </div>
          )}

          {!loading && !error && assets.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="assets-grid">
              {assets.map((asset) => {
                const imgUrl = getImageUrl(asset);
                if (isMediaTab(activeTab)) {
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => handleSelect(asset)}
                      className="flex flex-col items-center justify-center gap-2 p-3 bg-gray-800 rounded-lg border border-gray-700 hover:border-purple-500 transition-colors aspect-square"
                      data-testid={`asset-item-${asset.id}`}
                      title={asset.name || 'Select asset'}
                    >
                      <span className="text-[10px] uppercase tracking-wider text-purple-300">{selectedCategory === 'videos' ? 'Video' : 'Audio'}</span>
                      <span className="text-white text-xs text-center line-clamp-3 break-all">{asset.name || 'Untitled'}</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => handleSelect(asset)}
                    className="group relative bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-purple-500 transition-all hover:ring-1 hover:ring-purple-500/50 aspect-square"
                    data-testid={`asset-item-${asset.id}`}
                    title={asset.name || 'Select asset'}
                  >
                    <img
                      src={imgUrl}
                      alt={asset.name || 'Asset'}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden items-center justify-center w-full h-full bg-gray-800 text-gray-500">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                      <div className="w-full px-2 py-1.5 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs truncate">{asset.name || 'Untitled'}</p>
                      </div>
                    </div>
                    {/* Selection indicator on hover */}
                    <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
            data-testid="user-assets-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
